import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';


import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { exerciseService, workoutPlanService, ptClientService } from '../../services/supabaseService';
import { rapidApiGymFitService } from '../../services/rapidApiService';
import ExerciseCard from './components/ExerciseCard';
import WorkoutPlanCard from './components/WorkoutPlanCard';
import CreateExerciseModal from './components/CreateExerciseModal';
import CreateWorkoutPlanModal from './components/CreateWorkoutPlanModal';
import QuickAssignModal from './components/QuickAssignModal';
import FilterPanel from './components/FilterPanel';
import WorkoutPreviewPanel from './components/WorkoutPreviewPanel';

const WorkoutCatalog = () => {
  const { user, userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('exercises');
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [exercises, setExercises] = useState([]);
  const [workoutPlans, setWorkoutPlans] = useState([]);
  const [filteredExercises, setFilteredExercises] = useState([]);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [clients, setClients] = useState([]);
  
  // Modal states
  const [isCreateExerciseOpen, setIsCreateExerciseOpen] = useState(false);
  const [isCreatePlanOpen, setIsCreatePlanOpen] = useState(false);
  const [isQuickAssignOpen, setIsQuickAssignOpen] = useState(false);
  const [workoutToAssign, setWorkoutToAssign] = useState(null);
  
  // Advanced filters
  const [filters, setFilters] = useState({
    category: '',
    difficulty: '',
    muscleGroups: [],
    duration: '',
    equipment: [],
    goal: ''
  });

  const [favorites, setFavorites] = useState([]);
  const [recentlyUsed, setRecentlyUsed] = useState([]);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [showRecentOnly, setShowRecentOnly] = useState(false);

  // RapidAPI integration states
  const [useApiDatabase, setUseApiDatabase] = useState(false);
  const [apiExercises, setApiExercises] = useState([]);
  const [isApiLoading, setIsApiLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [isApiConfigured, setIsApiConfigured] = useState(false);

  const isTrainer = userProfile?.role === 'personal_trainer';

  useEffect(() => {
    // Check if RapidAPI is configured
    setIsApiConfigured(rapidApiGymFitService?.isConfigured());
    loadData();
    if (isTrainer) {
      loadClients();
    }
  }, [activeTab]);

  useEffect(() => {
    applyFilters();
  }, [exercises, workoutPlans, filters, searchQuery, showFavoritesOnly, showRecentOnly, apiExercises, useApiDatabase]);

  // Load API exercises when toggled
  useEffect(() => {
    if (useApiDatabase && activeTab === 'exercises' && isApiConfigured) {
      loadApiExercises();
    }
  }, [useApiDatabase, activeTab]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      if (activeTab === 'exercises') {
        const { data } = await exerciseService?.getExercises({});
        setExercises(data || []);
      } else {
        if (isTrainer) {
          const { data } = await workoutPlanService?.getWorkoutPlans(user?.id);
          setWorkoutPlans(data || []);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const { data } = await ptClientService?.getMyClients(user?.id);
      setClients(data || []);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const loadApiExercises = async () => {
    setIsApiLoading(true);
    setApiError(null);
    try {
      const { data, error } = await rapidApiGymFitService?.getAllExercises({
        category: filters?.category || undefined,
        muscle: filters?.muscleGroups?.[0] || undefined,
        equipment: filters?.equipment?.[0] || undefined,
        difficulty: filters?.difficulty || undefined
      });
      
      if (error) {
        setApiError(error);
        setApiExercises([]);
      } else {
        setApiExercises(data || []);
      }
    } catch (error) {
      console.error('Error loading API exercises:', error);
      setApiError('Failed to load exercises from API');
      setApiExercises([]);
    } finally {
      setIsApiLoading(false);
    }
  };

  const searchApiExercises = async (query) => {
    if (!query || query?.length < 2) {
      loadApiExercises();
      return;
    }
    
    setIsApiLoading(true);
    setApiError(null);
    try {
      const { data, error } = await rapidApiGymFitService?.searchExercises(query);
      
      if (error) {
        setApiError(error);
        setApiExercises([]);
      } else {
        setApiExercises(data || []);
      }
    } catch (error) {
      console.error('Error searching API exercises:', error);
      setApiError('Failed to search exercises');
      setApiExercises([]);
    } finally {
      setIsApiLoading(false);
    }
  };

  const applyFilters = () => {
    if (activeTab === 'exercises') {
      // Combine local and API exercises if API is enabled
      const sourceExercises = useApiDatabase ? apiExercises : exercises;
      let filtered = [...sourceExercises];

      // Search filter
      if (searchQuery) {
        if (useApiDatabase && isApiConfigured) {
          // For API, trigger search
          searchApiExercises(searchQuery);
          return;
        } else {
          // For local exercises, filter in memory
          filtered = filtered?.filter(ex => 
            ex?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
            ex?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
          );
        }
      }

      // Category filter
      if (filters?.category) {
        filtered = filtered?.filter(ex => ex?.category === filters?.category);
      }

      // Difficulty filter
      if (filters?.difficulty) {
        filtered = filtered?.filter(ex => ex?.difficulty === filters?.difficulty);
      }

      // Muscle groups filter
      if (filters?.muscleGroups?.length > 0) {
        filtered = filtered?.filter(ex => 
          filters?.muscleGroups?.some(mg => ex?.muscleGroups?.includes(mg))
        );
      }

      // Equipment filter
      if (filters?.equipment?.length > 0) {
        filtered = filtered?.filter(ex => 
          filters?.equipment?.some(eq => ex?.equipmentNeeded?.includes(eq))
        );
      }

      // Favorites filter
      if (showFavoritesOnly) {
        filtered = filtered?.filter(ex => favorites?.includes(ex?.id));
      }

      setFilteredExercises(filtered);
    } else {
      let filtered = [...workoutPlans];

      // Search filter
      if (searchQuery) {
        filtered = filtered?.filter(plan => 
          plan?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
          plan?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
        );
      }

      // Difficulty filter
      if (filters?.difficulty) {
        filtered = filtered?.filter(plan => plan?.difficulty === filters?.difficulty);
      }

      // Duration filter
      if (filters?.duration) {
        const [min, max] = filters?.duration?.split('-')?.map(Number);
        filtered = filtered?.filter(plan => {
          const duration = plan?.durationMinutes;
          return duration >= min && duration <= max;
        });
      }

      // Goal filter (tags)
      if (filters?.goal) {
        filtered = filtered?.filter(plan => 
          plan?.tags?.includes(filters?.goal)
        );
      }

      // Favorites filter
      if (showFavoritesOnly) {
        filtered = filtered?.filter(plan => favorites?.includes(plan?.id));
      }

      // Recently used filter
      if (showRecentOnly) {
        filtered = filtered?.filter(plan => recentlyUsed?.includes(plan?.id));
      }

      setFilteredPlans(filtered);
    }
  };

  const handleCreateExercise = async (exerciseData) => {
    const { data, error } = await exerciseService?.createExercise(exerciseData);
    if (!error && data) {
      setExercises(prev => [data, ...prev]);
      setIsCreateExerciseOpen(false);
    }
  };

  const handleCreatePlan = async (planData, planExercises) => {
    const { data, error } = await workoutPlanService?.createWorkoutPlan(planData, planExercises);
    if (!error && data) {
      setWorkoutPlans(prev => [data, ...prev]);
      setIsCreatePlanOpen(false);
    }
  };

  const handleQuickAssign = (workout) => {
    setWorkoutToAssign(workout);
    setIsQuickAssignOpen(true);
  };

  const handleAssignComplete = () => {
    setIsQuickAssignOpen(false);
    setWorkoutToAssign(null);
    // Add to recently used
    if (workoutToAssign && !recentlyUsed?.includes(workoutToAssign?.id)) {
      setRecentlyUsed(prev => [workoutToAssign?.id, ...prev]?.slice(0, 10));
    }
  };

  const toggleFavorite = (id) => {
    setFavorites(prev => 
      prev?.includes(id) ? prev?.filter(fav => fav !== id) : [...prev, id]
    );
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      difficulty: '',
      muscleGroups: [],
      duration: '',
      equipment: [],
      goal: ''
    });
    setSearchQuery('');
    setShowFavoritesOnly(false);
    setShowRecentOnly(false);
    if (useApiDatabase && isApiConfigured) {
      loadApiExercises();
    }
  };

  const resultCount = activeTab === 'exercises' ? filteredExercises?.length : filteredPlans?.length;

  return (
    <>
      <Helmet>
        <title>Workout Catalog - FormForge</title>
      </Helmet>
      <div className="min-h-screen" style={{ background: '#0A0A0B' }}>
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-4">
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#E8EAED' }}>Workout Catalog</h1>
              <p className="mt-2" style={{ color: '#6B7280' }}>{isTrainer ? 'Comprehensive exercise library and workout management hub' : 'Browse available exercises and workout plans'}</p>
            </div>
            {isTrainer && (
              <div className="flex gap-3">
                <Button onClick={() => setIsCreateExerciseOpen(true)}><Icon name="Plus" size={18} className="mr-2" />New Exercise</Button>
                <Button onClick={() => setIsCreatePlanOpen(true)} variant="outline"><Icon name="Plus" size={18} className="mr-2" />New Workout Plan</Button>
              </div>
            )}
          </div>

          {/* API Database Toggle */}
          {activeTab === 'exercises' && isApiConfigured && (
            <div className="mb-6 p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(0,212,255,0.1)' }}>
                    <Icon name="Database" size={20} style={{ color: '#00D4FF' }} />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold" style={{ color: '#E8EAED' }}>RapidAPI Exercise Database</h3>
                    <p className="text-xs" style={{ color: '#6B7280' }}>Access thousands of exercises from gym-fit API</p>
                  </div>
                </div>
                <button onClick={() => { setUseApiDatabase(!useApiDatabase); if (!useApiDatabase) loadApiExercises(); }} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors`} style={{ background: useApiDatabase ? '#FF6B00' : 'rgba(255,255,255,0.15)' }}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${useApiDatabase ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex gap-2">
              {[
                { id: 'exercises', icon: 'Dumbbell', label: 'Exercise Library' },
                ...(isTrainer ? [{ id: 'plans', icon: 'List', label: 'Workout Plans' }] : [])
              ]?.map(tab => (
                <button key={tab?.id} onClick={() => setActiveTab(tab?.id)} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all" style={activeTab === tab?.id ? { background: 'rgba(255,107,0,0.15)', color: '#FF6B00', border: '1px solid rgba(255,107,0,0.3)' } : { background: 'rgba(255,255,255,0.04)', color: '#6B7280', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Icon name={tab?.icon} size={18} />{tab?.label}
                </button>
              ))}
            </div>
            <div className="flex gap-2 ml-auto">
              {[{ id: 'grid', icon: 'Grid3x3' }, { id: 'list', icon: 'List' }]?.map(v => (
                <button key={v?.id} onClick={() => setViewMode(v?.id)} className="p-2 rounded-lg transition-colors" style={viewMode === v?.id ? { background: 'rgba(255,107,0,0.15)', color: '#FF6B00', border: '1px solid rgba(255,107,0,0.3)' } : { background: 'rgba(255,255,255,0.04)', color: '#6B7280', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Icon name={v?.icon} size={18} />
                </button>
              ))}
            </div>
          </div>

          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative">
              <Icon name="Search" size={20} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: '#6B7280' }} />
              <input type="text" placeholder={`Search ${activeTab === 'exercises' ? 'exercises' : 'workout plans'}...`} value={searchQuery} onChange={(e) => setSearchQuery(e?.target?.value)} className="w-full pl-12 pr-4 py-3 rounded-xl focus:outline-none focus:ring-2" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#E8EAED', focusRingColor: '#FF6B00' }} />
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-3">
              <FilterPanel activeTab={activeTab} filters={filters} setFilters={setFilters} showFavoritesOnly={showFavoritesOnly} setShowFavoritesOnly={setShowFavoritesOnly} showRecentOnly={showRecentOnly} setShowRecentOnly={setShowRecentOnly} resultCount={resultCount} onClearFilters={clearFilters} />
            </div>
            <div className="lg:col-span-6">
              {isLoading ? (
                <div className="flex justify-center items-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#FF6B00' }}></div>
                </div>
              ) : (
                <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 gap-4' : 'space-y-4'}>
                  {activeTab === 'exercises' ? (
                    filteredExercises?.length > 0 ? filteredExercises?.map((exercise) => (
                      <ExerciseCard key={exercise?.id} exercise={exercise} viewMode={viewMode} isFavorite={favorites?.includes(exercise?.id)} onToggleFavorite={() => toggleFavorite(exercise?.id)} onSelect={() => setSelectedWorkout({ type: 'exercise', data: exercise })} />
                    )) : (
                      <div className="col-span-full text-center py-20">
                        <Icon name="Dumbbell" size={48} className="mx-auto mb-4" style={{ color: '#6B7280' }} />
                        <p style={{ color: '#6B7280' }}>No exercises found</p>
                        <Button onClick={clearFilters} variant="outline" className="mt-4">Clear Filters</Button>
                      </div>
                    )
                  ) : (
                    filteredPlans?.length > 0 ? filteredPlans?.map((plan) => (
                      <WorkoutPlanCard key={plan?.id} plan={plan} viewMode={viewMode} isFavorite={favorites?.includes(plan?.id)} onToggleFavorite={() => toggleFavorite(plan?.id)} onQuickAssign={() => handleQuickAssign(plan)} onSelect={() => setSelectedWorkout({ type: 'plan', data: plan })} />
                    )) : (
                      <div className="col-span-full text-center py-20">
                        <Icon name="List" size={48} className="mx-auto mb-4" style={{ color: '#6B7280' }} />
                        <p style={{ color: '#6B7280' }}>No workout plans found</p>
                        <Button onClick={clearFilters} variant="outline" className="mt-4">Clear Filters</Button>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
            <div className="lg:col-span-3">
              <WorkoutPreviewPanel selectedWorkout={selectedWorkout} onClose={() => setSelectedWorkout(null)} onQuickAssign={isTrainer ? handleQuickAssign : null} />
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="lg:hidden">
            <div className="mb-4 flex gap-2">
              <button onClick={() => setShowFavoritesOnly(!showFavoritesOnly)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all" style={showFavoritesOnly ? { background: 'rgba(255,107,0,0.15)', color: '#FF6B00', border: '1px solid rgba(255,107,0,0.3)' } : { background: 'rgba(255,255,255,0.04)', color: '#6B7280', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Icon name="Star" size={16} />Favorites
              </button>
              <button onClick={clearFilters} className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm" style={{ background: 'rgba(255,255,255,0.04)', color: '#6B7280', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Icon name="X" size={16} />Clear
              </button>
            </div>
            {isLoading ? (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#FF6B00' }}></div>
              </div>
            ) : (
              <div className="space-y-4">
                {activeTab === 'exercises' ? filteredExercises?.map((exercise) => (
                  <ExerciseCard key={exercise?.id} exercise={exercise} viewMode="list" isFavorite={favorites?.includes(exercise?.id)} onToggleFavorite={() => toggleFavorite(exercise?.id)} onSelect={() => setSelectedWorkout({ type: 'exercise', data: exercise })} />
                )) : filteredPlans?.map((plan) => (
                  <WorkoutPlanCard key={plan?.id} plan={plan} viewMode="list" isFavorite={favorites?.includes(plan?.id)} onToggleFavorite={() => toggleFavorite(plan?.id)} onQuickAssign={() => handleQuickAssign(plan)} onSelect={() => setSelectedWorkout({ type: 'plan', data: plan })} />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
      {isCreateExerciseOpen && <CreateExerciseModal onClose={() => setIsCreateExerciseOpen(false)} onCreate={handleCreateExercise} />}
      {isCreatePlanOpen && <CreateWorkoutPlanModal onClose={() => setIsCreatePlanOpen(false)} onCreate={handleCreatePlan} exercises={exercises} />}
      {isQuickAssignOpen && workoutToAssign && <QuickAssignModal workout={workoutToAssign} clients={clients} onClose={() => setIsQuickAssignOpen(false)} onAssignComplete={handleAssignComplete} />}
    </>
  );
};

export default WorkoutCatalog;